// client/src/pages/secure/OperationsDocsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser, canManageStaff } from '../../hooks/useCurrentUser';
import { ProtectedLink } from '../../utils/openProtected';
import DataList from '../../components/shared/DataList';

// --- Upload Document Modal Component ---
const UploadDocModal = ({ isOpen, onClose, onDocumentUploaded }) => {
    const [file, setFile] = useState(null);
    const [documentName, setDocumentName] = useState('');
    const [category, setCategory] = useState('Form');
    const [description, setDescription] = useState('');
    const [version, setVersion] = useState('');
    const [error, setError] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const apiClient = useApiClient();

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setDocumentName(selectedFile.name.replace(/\.[^/.]+$/, ""));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) {
            setError('Please select a file to upload.');
            return;
        }
        setError('');
        setIsUploading(true);

        const formData = new FormData();
        formData.append('documentFile', file);
        formData.append('documentName', documentName);
        formData.append('category', category);
        formData.append('description', description);
        formData.append('version', version);

        try {
            const result = await apiClient.post('/api/operations/documents', formData, true);
            onDocumentUploaded(result);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsUploading(false);
        }
    };
    
    useEffect(() => {
        if (isOpen) {
            setFile(null);
            setDocumentName('');
            setCategory('Form');
            setDescription('');
            setVersion('');
            setError('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Upload Operations Document</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input value={documentName} onChange={(e) => setDocumentName(e.target.value)} placeholder="Document Name" required className="p-2 border rounded w-full" />
                    <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full p-2 border rounded">
                        <option>Form</option>
                        <option>Disclosure</option>
                        <option>Policy</option>
                        <option>Template</option>
                        <option>Training</option>
                        <option>Other</option>
                    </select>
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (Optional)" rows="3" className="p-2 border rounded w-full"></textarea>
                    <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="Version (e.g., v1.0)" className="p-2 border rounded w-full" />
                    <input type="file" onChange={handleFileChange} required className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"/>
                    
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isUploading} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isUploading ? 'Uploading...' : 'Upload'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Edit Document Modal Component ---
const EditDocModal = ({ isOpen, onClose, onDocumentUpdated, doc }) => {
    const [formData, setFormData] = useState({ document_name: '', category: '', description: '', version: '' });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const apiClient = useApiClient();

    useEffect(() => {
        if (doc) {
            setFormData({
                document_name: doc.document_name,
                category: doc.category,
                description: doc.description || '',
                version: doc.version || ''
            });
        }
    }, [doc]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');
        try {
            const updatedDoc = await apiClient.put(`/api/operations/documents/${doc.document_id}`, formData);
            onDocumentUpdated(updatedDoc);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to update document.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg">
                <h2 className="text-2xl font-bold text-slate-800 mb-6">Edit Document Details</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input name="document_name" value={formData.document_name} onChange={handleChange} placeholder="Document Name" required className="p-2 border rounded w-full" />
                    <select name="category" value={formData.category} onChange={handleChange} className="w-full p-2 border rounded">
                        <option>Form</option> <option>Disclosure</option> <option>Policy</option>
                        <option>Template</option> <option>Training</option> <option>Other</option>
                    </select>
                    <textarea name="description" value={formData.description} onChange={handleChange} placeholder="Description" rows="3" className="p-2 border rounded w-full"></textarea>
                    <input name="version" value={formData.version} onChange={handleChange} placeholder="Version" className="p-2 border rounded w-full" />
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-slate-800 text-white rounded disabled:bg-indigo-400">
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Main Operations Documents Page Component ---
const OperationsDocsPage = () => {
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const apiClient = useApiClient();

    const fetchDocuments = useCallback(() => {
        apiClient.get('/api/operations/documents')
            .then(data => setDocuments(data))
            .catch(err => setError(err.message || 'Failed to fetch documents.'))
            .finally(() => setIsLoading(false));
    }, [apiClient]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);
    
    const handleDocumentUploaded = (newDocument) => {
        fetchDocuments(); // Refetch all documents to ensure correct sorting
    };

    const handleOpenEditModal = (doc) => {
        setSelectedDoc(doc);
        setIsEditModalOpen(true);
    };

    const handleDocumentUpdated = (updatedDoc) => {
        setDocuments(prev => prev.map(doc => doc.document_id === updatedDoc.document_id ? updatedDoc : doc));
    };

    const handleDelete = async (docId) => {
        if (window.confirm('Remove this document from the Doc Cabinet? It is kept on file (records retention), just no longer listed.')) {
            try {
                await apiClient.delete(`/api/operations/documents/${docId}`);
                setDocuments(prevDocs => prevDocs.filter(doc => doc.document_id !== docId));
            } catch (err) {
                alert(err.message || 'Failed to delete document.');
            }
        }
    };


    return (
        <>
            <UploadDocModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onDocumentUploaded={handleDocumentUploaded}
            />
            <EditDocModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onDocumentUpdated={handleDocumentUpdated}
                doc={selectedDoc}
            />
            <div className="space-y-8">
                <div className="flex justify-between items-center">
                    <h1 className="text-3xl font-bold text-slate-800">Operations Documents Cabinet</h1>
                    {canManageStaff(getCurrentUser()) && (
                        <button onClick={() => setIsUploadModalOpen(true)} className="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded-md hover:bg-slate-900">
                            Upload New Document
                        </button>
                    )}
                </div>

                {isLoading && <p>Loading documents...</p>}
                {error && <p className="text-red-500">{error}</p>}
                
                {!isLoading && !error && (
                    <div className="bg-white p-6 rounded-lg shadow">
                        <DataList rows={documents} rowKey="document_id" csvName="operations-documents" defaultSort={{ key: 'category', dir: 'asc' }} empty="No documents yet."
                            filters={[{ key: 'category', label: 'Category' }]}
                            columns={[
                                { key: 'category', label: 'Category' },
                                { key: 'document_name', label: 'Document Name', render: (doc) => <span className="font-medium text-slate-900">{doc.document_name}</span> },
                                { key: 'description', label: 'Description', render: (doc) => <span className="block max-w-md truncate text-slate-500" title={doc.description}>{doc.description}</span> },
                                { key: 'version', label: 'Version' },
                                { key: 'actions', label: 'Actions', sortable: false, csv: false, search: false, render: (doc) => (
                                    <span className="whitespace-nowrap space-x-4">
                                        {canManageStaff(getCurrentUser()) && <button onClick={() => handleOpenEditModal(doc)} className="text-indigo-600 hover:text-indigo-900">Edit</button>}
                                        <ProtectedLink href={doc.storage_path} className="text-indigo-600 hover:text-indigo-900">Download</ProtectedLink>
                                        {canManageStaff(getCurrentUser()) && <button onClick={() => handleDelete(doc.document_id)} className="text-red-600 hover:text-red-900">Delete</button>}
                                    </span>) },
                            ]} />
                    </div>
                )}
            </div>
        </>
    );
};

export default OperationsDocsPage;